/**
 * Footer CREDITS indicator (sc-6943 follow-up).
 *
 * Owns the `#cabinet-credit` slot in the cabinet footer. The value was
 * previously a hardcoded `∞` in index.html — decorative, never reflecting
 * the player's real state. Now it reads the wallet and shows:
 *
 *   - active pass          → "CREDITS ∞"    (unlimited; NOT clickable)
 *   - signed in, N tokens  → "CREDITS N"     (clickable → buy modal)
 *   - anon, N free tastes  → "FREE PLAY N"   (clickable → login wall)
 *   - anon, taste used     → "FREE PLAY 0"   (clickable → login wall)
 *   - wallet/meter error   → hidden
 *
 * The anon "free taste" (FREE_TASTE_PER_DAY, default 1) is a DIFFERENT
 * thing from the signed-in daily token wallet (5/day) — hence the distinct
 * "FREE PLAY" label. It's read-only here (peekTaste, no consume); the gate
 * still does the atomic spend at launch.
 *
 * Refreshes on: boot, session change (wired here), and whenever a caller
 * invokes refreshFooterCredits() — after a run ends (a token/taste was
 * spent) or a purchase relaunches (a pass may now be active).
 */

import { getWallet, peekTaste } from '../api/client.js';
import { isSignedIn, onSessionChange } from '@arcraz/arcadia-auth-web';
import { showLoginWall, showPaywall } from './play-gate-wall.js';
import { track } from '../runtime/analytics.js';

const INFINITY = '∞';

function slot(): HTMLElement | null {
  const el = document.getElementById('cabinet-credit');
  return el instanceof HTMLElement ? el : null;
}

/** Paint a static (non-interactive) label — the ∞ pass state. */
function setStatic(el: HTMLElement, text: string): void {
  el.hidden = false;
  el.textContent = text;
  el.classList.remove('control-panel__credit--clickable');
  el.removeAttribute('role');
  el.removeAttribute('tabindex');
  el.removeAttribute('aria-label');
  el.onclick = null;
  el.onkeydown = null;
}

/** Paint a clickable label that runs `open` on click / Enter / Space. */
function setClickable(el: HTMLElement, text: string, ariaLabel: string, open: () => void): void {
  el.hidden = false;
  el.textContent = text;
  el.classList.add('control-panel__credit--clickable');
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.setAttribute('aria-label', ariaLabel);
  el.onclick = open;
  el.onkeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };
}

/** Hide the indicator (meter/wallet couldn't be read). */
function hide(el: HTMLElement): void {
  el.hidden = true;
  el.textContent = '';
  el.classList.remove('control-panel__credit--clickable');
  el.removeAttribute('role');
  el.removeAttribute('tabindex');
  el.removeAttribute('aria-label');
  el.onclick = null;
  el.onkeydown = null;
}

/**
 * Paint the anon "FREE PLAY N" indicator — the daily free taste, clickable
 * to the login wall (sign in for the full 5/day). Read-only peek; the gate
 * still does the atomic spend at launch.
 */
async function renderAnon(el: HTMLElement): Promise<void> {
  let taste;
  try {
    taste = await peekTaste();
  } catch {
    hide(el);
    return;
  }
  if (isSignedIn()) return; // session flipped mid-request; the signed-in path will repaint
  setClickable(el, `FREE PLAY ${taste.remaining}`, `${taste.remaining} free play remaining — sign in for 5 a day`, () => {
    track('login_wall_shown', { source: 'footer_credits' });
    void showLoginWall(undefined, undefined, refreshFooterCredits);
  });
}

/**
 * Read the wallet/meter and paint the indicator. Never throws — a read
 * failure just hides the credit rather than surfacing an error in the chrome.
 */
export async function refreshFooterCredits(): Promise<void> {
  const el = slot();
  if (!el) return;

  if (!isSignedIn()) {
    await renderAnon(el);
    return;
  }

  let wallet;
  try {
    wallet = await getWallet();
  } catch {
    hide(el); // fail quiet in the chrome; the gate handles fail-closed
    return;
  }

  // Session may have changed while the request was in flight.
  if (!isSignedIn()) {
    await renderAnon(el);
    return;
  }

  if (wallet.entitlement) {
    // Unlimited — no purchase to make, so not interactive.
    setStatic(el, `CREDITS ${INFINITY}`);
    return;
  }

  // Token player — show the count and make it a shortcut to the buy modal.
  // Copy adapts: 0 left → the honest "out of tokens" framing; tokens remaining
  // → the voluntary "go unlimited" upgrade framing (not "you're out").
  const reason = wallet.tokensRemaining > 0 ? 'upgrade' : 'exhausted';
  setClickable(
    el,
    `CREDITS ${wallet.tokensRemaining}`,
    `${wallet.tokensRemaining} credits remaining — get a pass`,
    () => {
      track('paywall_shown', { source: 'footer_credits' });
      void showPaywall(wallet.resetsAtUtc, undefined, reason).then(refreshFooterCredits);
    }
  );
}

/** Wire the indicator: initial paint + repaint on every session change. */
export function wireFooterCredits(): void {
  void refreshFooterCredits();
  onSessionChange(() => void refreshFooterCredits());
}
